import React, { FC, PropsWithChildren, useContext, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useDialog } from "@natera/platform/lib/hooks";

import TestNotFoundModal from "@app/components/testNotFoundDialog/testNotFound";
import { TestDetailsContext } from "@app/provider";
import { TestCardStatus } from "@app/provider/testData/types";

/**
 * Client-side gate for result-related routes (result-details, result-pdf, understanding-your-results, share-fetal-sex).
 *
 * When the test card status is PROCESSING (result release is delayed), we
 * render the generic "Test not found" dialog instead of the wrapped page,
 * mirroring the server which returns 404/TEST_NOT_FOUND for the same condition.
 *
 * Supports two usage patterns:
 *   - Children wrapper: <DelayedResultReleaseGuard><Page /></DelayedResultReleaseGuard>
 *   - Layout route:     <Route element={<DelayedResultReleaseGuard />}>...</Route>
 *
 * The guard relies on TestDetailsContext having already loaded the test card
 * (TestDetailsProvider gates its own children on this), so testCard?.status is
 * reliably available by the time this renders.
 */
const DelayedResultReleaseGuard: FC<PropsWithChildren> = ({ children }) => {
  const { getTestDetails } = useContext(TestDetailsContext);
  const testCard = getTestDetails();
  const isResultReleaseDelayed = testCard?.status === TestCardStatus.PROCESSING;

  const testNotFoundDialog = useDialog(TestNotFoundModal);

  useEffect(() => {
    if (isResultReleaseDelayed) {
      testNotFoundDialog.open({});
    }
  }, [isResultReleaseDelayed]);

  if (isResultReleaseDelayed) {
    return <>{testNotFoundDialog.getDialog()}</>;
  }

  return <>{children ?? <Outlet />}</>;
};

export default DelayedResultReleaseGuard;
